/* Sticky add-to-cart bar controller (Asana style-updates item 8).

   - Shows the bar once the real buy button has scrolled above the viewport.
   - Mirrors the live title (incl. the fitment year prefix), the current
     variant price and the real Add-to-Cart button's label + availability, so
     the sticky bar always matches the product form. The theme re-renders the
     title / price / <product-form> on every variant change and fires the
     `product:variant-change` document event, and the fitment stepper toggles
     `.fitment-locked`; we re-sync on that event and via a MutationObserver, and
     re-query the live nodes every time (never cache the replaced ones).
   - Clicking the sticky button forwards to the real Add-to-Cart button (so the
     theme cart + fitment lock keep working); while locked it scrolls the
     shopper to the vehicle selector instead. */
(function () {
  'use strict';

  function init() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    var stickyTitle = bar.querySelector('.sticky-atc__title');
    var stickyPrice = bar.querySelector('.sticky-atc__price');
    var stickyAdd = bar.querySelector('.sticky-atc__add');
    var section = bar.closest('.shopify-section') || document;
    var defaultLabel = stickyAdd.textContent.trim() || 'Add to Cart';

    // Anchor the bar to the sticky header section (the element that actually
    // sticks — `header.header` itself is position:relative inside it). As an
    // absolutely positioned child hanging off its bottom edge, the bar always
    // travels WITH the header: during rubber-band overscroll at the bottom of
    // the page a sticky header is pulled up with the content, and a
    // position:fixed bar stayed behind, opening a gap (client feedback round 2,
    // item 5). Note `section` above is the PRODUCT section and is captured
    // before this move — the MutationObserver must keep watching that one.
    var headerHost = null;
    var clip = null;
    var stickyHeader = document.querySelector('header.header');
    if (stickyHeader) headerHost = stickyHeader.closest('.shopify-section');
    if (headerHost) {
      // The hidden bar is parked above its anchor (translateY(-100%)) and must be
      // masked there. Relying on the header painting over it does NOT work: the
      // section also spans the YMM bar's band, which fades to opacity 0 while
      // scrolling, leaving the parked bar in plain sight below the header. So clip
      // it with a wrapper of exactly the bar's height instead — independent of
      // what any ancestor paints.
      clip = document.createElement('div');
      clip.className = 'sticky-atc-clip';
      bar.classList.add('sticky-atc--in-header');
      headerHost.appendChild(clip);
      clip.appendChild(bar);
    } else if (bar.parentNode !== document.body) {
      // Fallback (header markup not as expected): keep the old body + fixed
      // positioning, offset below the header on every frame while visible.
      document.body.appendChild(bar);
    }

    function realForm() {
      return document.querySelector('product-form');
    }
    function realAtc() {
      return document.querySelector('.product-actions-add-to-cart button[name="add"]');
    }
    function buyArea() {
      return (
        document.querySelector('.product-actions-add-to-cart') ||
        document.querySelector('product-form')
      );
    }
    function titleEl() {
      return document.querySelector('.product-info-heading');
    }
    function priceEl() {
      return document.querySelector('.product-block-price .price');
    }
    /* The thumbnail is deliberately NOT synced with the gallery: the client
       wants the sticky bar to always show the product's main image, not
       whichever image happens to be selected (client feedback round 2, item 4).
       It stays whatever snippets/sticky-atc.liquid rendered. */

    function sync() {
      // Title (mirrors the H1, which the fitment stepper prefixes with the year)
      var t = titleEl();
      if (t) {
        var text = t.textContent.replace(/\s+/g, ' ').trim();
        if (text) stickyTitle.textContent = text;
      }

      // Price — the effective (sale or regular) amount only
      var p = priceEl();
      if (p) {
        var amountEl = p.querySelector('.price-item-last') || p.querySelector('.price-item-regular');
        stickyPrice.textContent = amountEl ? amountEl.textContent.trim() : '';
      } else {
        stickyPrice.textContent = '';
      }

      // Button — mirror label + availability / lock state
      var atc = realAtc();
      var form = realForm();
      var locked = !!(form && form.classList.contains('fitment-locked'));
      var disabled = !!(atc && atc.hasAttribute('disabled'));
      if (locked) {
        // Editing the vehicle: mirror the neutral, clickable "Add to Cart" that
        // the real button shows (clicking scrolls to the selector).
        stickyAdd.textContent = defaultLabel;
      } else if (atc) {
        var label = (atc.querySelector('span') || atc).textContent.trim();
        stickyAdd.textContent = label || defaultLabel;
      }
      // Look blocked whenever the real button is blocked — disabled (out of
      // stock / unavailable) OR fitment-locked (no vehicle chosen yet).
      stickyAdd.classList.toggle('is-disabled', disabled || locked);

      // The title can wrap to a second line, changing the bar's height and so the
      // clipping window's.
      updateAnchor();
    }

    // Where the bar hangs inside the header section. The section is taller than
    // the visible header: it also reserves ~92px for the YMM ("Shop My Vehicle")
    // bar, which is opacity:0/visibility:hidden on most product pages but still
    // occupies its box — so the section's own height is not the anchor. Take the
    // bottom of the last VISIBLE child instead, which also keeps the bar below
    // the YMM bar on pages/breakpoints where it IS shown.
    // Deliberately uses layout offsets, not getBoundingClientRect: offsetTop is
    // scroll-independent, so overscroll can't skew it (that was the bug).
    function updateAnchor() {
      if (!headerHost) return;
      var bottom = null;
      var kids = headerHost.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el === clip || !el.offsetHeight || !el.offsetParent) continue;
        // `easy-hidden` is the class the YMM widget uses to collapse itself. Check
        // it BEFORE the computed style: the collapse is a 0.5s opacity/visibility
        // transition, so mid-flight the computed values still read as visible and
        // the anchor would be left stale at the taller value.
        if (el.classList.contains('easy-hidden')) continue;
        var cs = getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.opacity === '0') continue;
        var b = el.offsetTop + el.offsetHeight;
        if (bottom === null || b > bottom) bottom = b;
      }
      if (bottom === null) bottom = headerHost.offsetHeight;
      // -1px so the bar tucks under the header with no seam (no content peek).
      clip.style.top = bottom - 1 + 'px';
      clip.style.height = bar.offsetHeight + 'px';
    }

    // Fallback path only: height of the sticky header, so a position:fixed bar
    // can be offset to sit directly beneath it. Unused when the bar lives inside
    // the header section.
    function headerOffset() {
      var h = document.querySelector('header.header') || document.querySelector('.section-header');
      if (!h) return 0;
      return Math.max(0, Math.round(h.getBoundingClientRect().bottom));
    }

    // Visibility: show once the buy button is scrolled above the viewport top.
    var ticking = false;
    var visible = false;
    function updateVisibility() {
      ticking = false;
      var el = buyArea();
      if (!el) return;
      var show = el.getBoundingClientRect().bottom < 0;
      if (!headerHost) {
        // -1px so the bar tucks under the header with no seam (no content peek).
        bar.style.transform = show ? 'translateY(' + (headerOffset() - 1) + 'px)' : '';
      }
      if (show === visible) return;
      visible = show;
      if (show) updateAnchor();
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateVisibility);
      }
    }
    // In the fallback path the offset has to be re-measured continuously: no
    // scroll event fires during rubber-band overscroll, which is exactly when
    // the header moves out from under a fixed bar.
    if (!headerHost) {
      (function follow() {
        if (visible) bar.style.transform = 'translateY(' + (headerOffset() - 1) + 'px)';
        window.requestAnimationFrame(follow);
      })();
    }

    // Forward the click to the real button (or nudge to the selector if locked).
    stickyAdd.addEventListener('click', function () {
      var form = realForm();
      if (form && form.classList.contains('fitment-locked')) {
        var target =
          document.querySelector('.fitment-stepper') ||
          document.querySelector('.product-actions-add-to-cart');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      var atc = realAtc();
      if (atc) atc.click();
    });

    // Re-sync on variant change + any relevant DOM mutation (title/price/form swap).
    document.addEventListener('product:variant-change', function () {
      setTimeout(sync, 60);
    });
    var debounce;
    var mo = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(sync, 80);
    });
    // Watch attributes too — the fitment stepper toggles `.fitment-locked` on
    // <product-form> and the theme flips the button's `disabled` after load.
    mo.observe(section, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'disabled']
    });

    // The YMM bar can be toggled open inside the header (mobile "Shop My
    // Vehicle"), which changes where the bar has to hang.
    if (headerHost) {
      var anchorDebounce;
      new MutationObserver(function () {
        clearTimeout(anchorDebounce);
        anchorDebounce = setTimeout(updateAnchor, 60);
      }).observe(headerHost, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
      // The collapse is animated; re-measure once it has actually finished.
      headerHost.addEventListener('transitionend', updateAnchor);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      updateAnchor();
      onScroll();
    });

    sync();
    updateAnchor();
    updateVisibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
